import React, { useEffect, useRef, useState } from 'react';
import { Animated, Image, ImageBackground, Linking, View } from 'react-native';

import B from '../../../components/Bold';
import { Button, Text, TextInput, useTheme } from 'react-native-paper';
import GradientText from '../../../components/GradientText';
import { NavigationWithProps } from '../../../core/Types';
import { AppleAuthenticateRequest, useAuth } from '../../../core/api/auth';
import { CredentialResponse } from '@react-oauth/google';
import GoogleSignInButton from '../../../components/GoogleSigninButton';
import { LoginNavParamList } from '../Login';
import { useToast } from 'react-native-toast-notifications';
import AppleSignInButton from '../../../components/AppleSignInButton';
import { SignInResponseI } from '../../../../apple-sign-in';
import { getGoogleUser } from '../../../core/jwt/jwt';
import { ApiErrors } from '../../../core/models/ApiResponse';
import { areWeInIphone } from '../../../utils/Native';
import { Pet } from '../../../assets/images';

type AuthProvider = "apple" | "google";

const AuthorizationStep: React.FC<NavigationWithProps<{}, LoginNavParamList, "Authorization">> = ({ navigation, route }) => {

    const { therapistId, clinicId } = route.params || {};

    const opacityAnim = useRef(new Animated.Value(0)).current;

    const theme = useTheme();
    const auth = useAuth();
    const toast = useToast();

    const [loginEmail, setLoginEmail] = useState<string>("");
    const [loading, setLoading] = useState<AuthProvider>();

    const onSuccess = async (
        response: CredentialResponse | SignInResponseI,
        provider: AuthProvider
    ) => {
        let credential: string | AppleAuthenticateRequest;
        let loginPromise: Promise<void>;
        let userName: string | undefined;
        if (provider === "google") {
            credential = (response as CredentialResponse).credential!;
            userName = getGoogleUser(credential).name;
            loginPromise = auth.googleLogin(credential, true);
        } else if (provider === "apple") {
            response = response as SignInResponseI;
            credential = {
                ...response.authorization,
                full_name: response.user?.name
                    ? `${response.user?.name?.firstName} ${response.user?.name?.lastName}`
                    : "",
            };
            userName = response.user?.name?.firstName;
            loginPromise = auth.appleLogin(credential, true);
        } else {
            console.error("Invalid provider");
            return;
        }

        setLoading(provider);
        await loginPromise
            .then(() => {
                if (therapistId && clinicId) {
                    location.replace(`/therapist?id=${therapistId}&clinic=${clinicId}`);
                } else {
                    location.replace("/");
                }
            })
            .catch(onError)
            .finally(() => {
                setLoading(undefined)
            });
    };

    const onError = () => {
        console.log('Error:', 'Error');
        toast.show("Error al iniciar sesión", { duration: 1000 });
    }

    const loginOrSignupWithEmail = () => {
        if (loginEmail.trim() === "") {
            toast.show("Por favor, ingresa un email válido", { duration: 1000 });
            return;
        }

        auth.emailVerificationForSignup(loginEmail)
            .then((verificationToken: string) => {
                navigation.navigate("SignUpEmail", { email: loginEmail, verif: verificationToken, therapistId, clinicId });
            })
            .catch(async (e) => {
                console.log("Error during email verification:", e);
                if (e.error === ApiErrors.EMAIL_EXISTS) {
                    navigation.navigate("LoginEmail", { email: loginEmail, therapistId, clinicId });
                }
            })
            .catch(onError);
    };

    const signupWithEmail = () => {
        navigation.navigate("SignUpEmail", { therapistId, clinicId });
    };

    const openTermsAndConditions = () => {
        Linking.openURL("https://psicopro.co/terminos-y-condiciones/");
    };

    const openPrivacyPolicy = () => {
        Linking.openURL("https://psicopro.co/politica-de-privacidad/");
    };

    useEffect(() => {
        Animated.timing(opacityAnim, {
            toValue: 1,
            duration: 1000,
            useNativeDriver: false
        }).start();
    }, []);

    return (
        <Animated.View style={{ height: "100%", opacity: opacityAnim }}>
            <ImageBackground
                style={{
                    height: "100%",
                    flexDirection: 'column',
                }}
                source={require("../../../../assets/img/scenes/fondo_marco.png")}>
                <Text style={{
                    fontSize: 30,
                    textAlign: 'center',
                    marginTop: "20%",
                    marginHorizontal: 20,
                }}>
                    Hola, soy <GradientText style={{
                        fontSize: 30,
                        fontWeight: 'bold',
                    }} color1={'#3e7200'} color2={'#112D00'}><B>Kyra</B></GradientText>,
                    me alegra que estés aquí
                </Text>
                <View style={{
                    alignItems: 'center',
                    marginTop: 20,
                }}>
                    <Image style={{
                        width: 200,
                        height: 200
                        ,
                    }} source={Pet.greetingsFront} />
                </View>
                <View style={{
                    marginTop: "auto",
                    marginBottom: 100
                }}>
                    <Text style={{
                        fontSize: 20,
                        textAlign: 'center',
                        marginBottom: 10,
                        color: theme.colors.onTertiaryContainer,
                    }}>
                        <B>Inicia sesión para continuar</B>
                    </Text>
                    <View style={{
                        marginHorizontal: 20,
                        marginBottom: 10
                    }}>
                        <TextInput
                            style={{ flex: 1 }}
                            label="Email"
                            value={loginEmail}
                            onChangeText={text => setLoginEmail(text)}
                            mode="outlined"
                        />
                        <Button
                            style={{ marginTop: 10, borderRadius: 5 }}
                            mode="contained"
                            onPress={loginOrSignupWithEmail}
                        >
                            Continuar con email
                        </Button>
                    </View>
                    <View style={{
                        marginHorizontal: 20,
                    }}>
                        <Text style={{
                            fontSize: 14,
                            textAlign: "center",
                        }}>
                            ¿No tienes una cuenta? <Text onPress={signupWithEmail}><B>Regístrate</B></Text>
                        </Text>
                    </View>

                    <View style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginHorizontal: 20,
                        marginBottom: 10,
                    }}>
                        <View style={{ flex: 1, height: 1, backgroundColor: theme.colors.onTertiaryContainer }} />
                        <Text style={{ marginHorizontal: 10, color: theme.colors.onTertiaryContainer }}>o</Text>
                        <View style={{ flex: 1, height: 1, backgroundColor: theme.colors.onTertiaryContainer }} />
                    </View>
                    {!areWeInIphone() &&
                        <View style={{
                            marginHorizontal: 20,
                            marginBottom: 10
                        }}>
                            <GoogleSignInButton
                                onSuccess={c => onSuccess(c, "google")}
                                onError={onError}
                                loading={loading === "google"}
                            />
                        </View>
                    }
                    <View style={{
                        marginHorizontal: 20,
                        marginBottom: 10
                    }}>
                        <AppleSignInButton
                            onSuccess={c => onSuccess(c, "apple")}
                            onError={onError}
                            loading={loading === "apple"}
                        />
                    </View>
                    <Text style={{
                        marginHorizontal: 20,
                        fontSize: 14,
                        textAlign: "center",
                    }}>
                        Continuando aceptas los <Text onPress={openTermsAndConditions}><B>Términos y Condiciones</B> </Text>
                        y la <Text onPress={openPrivacyPolicy}><B>Política de privacidad</B></Text>.
                    </Text>
                </View>
            </ImageBackground>
        </Animated.View >
    );
}

export default AuthorizationStep;
